import React from 'react'
import { Button, Input, Select, message } from 'antd'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import './singleLocation.scss'
import { Collapse } from 'reactstrap'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { ILocationContract } from '@getgreenline/locations/build/models'
import { CompaniesModels } from '@getgreenline/companies'
import { LocationModels } from '@getgreenline/locations'
import { Col, Row } from 'antd-v4'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  index: number
  location?: ILocationContract
  onSave: () => void
}

interface State {
  name: string
  address: string | null
  description: string | null
  timezone: string | null
  expanded: boolean
  isSaving: boolean
  storeId: string | null
  city: string | null
  province: CompaniesModels.Province | null
  postalCode: string | null
  phoneNumber: string | null
  email: string | null
}

function swap(array: ILocationContract[], index1: number, index2: number) {
  const b = array[index1]
  array[index1] = array[index2]
  array[index2] = b
}

@inject('currentCompanyStore')
@observer
export class SingleLocation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    const { location } = this.props

    this.state = {
      name: location ? location.name : '',
      address: location ? location.address : null,
      description: location ? location.description : null,
      timezone: location ? location.timezone : null,
      expanded: props.location ? false : true, // If creating a location, start off expanded
      isSaving: false,
      storeId: location ? location.storeId : '',
      city: location ? location.city : '',
      province: location ? location.province : null,
      postalCode: location ? location.postalCode : '',
      phoneNumber: location ? location.phoneNumber : null,
      email: location ? location.email : null,
    }
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (prevProps.location !== this.props.location) {
      const { location } = this.props

      this.setState({
        name: location ? location.name : '',
        address: location ? location.address : null,
        description: location ? location.description : null,
        timezone: location ? location.timezone : null,
      })
    }
  }

  reorderUp = async (index: number) => {
    const locations = this.props.currentCompanyStore!.locations!
    if (index > 0) {
      swap(locations, index, index - 1)
      this.saveReorder()
    }
  }

  reorderDown(index: number) {
    const locations = this.props.currentCompanyStore!.locations!
    if (index < locations.length - 1) {
      swap(locations, index, index + 1)
      this.saveReorder()
    }
  }

  editLocation = async (location: LocationModels.ILocationContract) => {
    await this.props.currentCompanyStore!.editLocation({
      id: location.id,
      companyId: location.companyId,
      name: this.state.name,
      address: this.state.address || '',
      description: this.state.description || '',
      timezone: this.state.timezone!,
      storeId:
        this.state.province == CompaniesModels.Province.ON
          ? this.state.storeId?.trim()
          : this.state.storeId || '',
      city: this.state.city || '',
      province: this.state.province || null,
      postalCode: this.state.postalCode || '',
      phoneNumber: this.state.phoneNumber || null,
      email: this.state.email || null,
    })
  }

  addLocation = async () => {
    await this.props.currentCompanyStore!.addLocation({
      name: this.state.name,
      address: this.state.address,
      description: this.state.description,
      timezone: this.state.timezone,
      storeId:
        this.state.province == CompaniesModels.Province.ON
          ? this.state.storeId?.trim()
          : this.state.storeId || '',
      city: this.state.city,
      province: this.state.province,
      postalCode: this.state.postalCode,
      phoneNumber: this.state.phoneNumber,
      email: this.state.email,
    })
  }

  save = async () => {
    const { location } = this.props
    this.setState({ isSaving: true }, async () => {
      try {
        if ((this.state.postalCode || '').length > 10) {
          return message.error('Postal code must be less than 10 characters')
        }
        if (location) {
          await this.editLocation(location)
        } else {
          await this.addLocation()
        }

        this.setState({ expanded: false })
        message.success(`${this.state.name} successfully updated.`, 1)

        this.props.onSave()
      } catch (error) {
        message.error(
          parseErrorMsg(error) || `There was an error while updating ${this.state.name}`,
        )
      } finally {
        this.setState({ isSaving: false })
      }
    })
  }

  saveReorder = async () => {
    const { locations } = this.props.currentCompanyStore!
    if (!locations) {
      return
    }

    const locationIndexMapping = locations.map((location, index) => {
      return {
        locationId: location.id,
        index: index,
      }
    })

    try {
      await this.props.currentCompanyStore!.reorderLocations(locationIndexMapping)
      this.setState({ expanded: false })
      this.props.onSave()
    } catch (error) {
      message.error(
        parseErrorMsg(error) || `There was an error while re-ordering ${this.state.name}`,
      )
    }
  }

  delete = async () => {
    const { location } = this.props
    if (!location) {
      return
    }

    if (
      window.confirm(
        'Are you sure? You may lose access to historical data. It is recommended that you simply rename the location.',
      )
    ) {
      this.setState({ isSaving: true }, async () => {
        try {
          await this.props.currentCompanyStore!.deleteLocation(location.id)
          message.success(`${this.state.name} successfully deleted.`)
        } catch (error) {
          message.error(
            parseErrorMsg(error) || `There was an error while deleting ${this.state.name}`,
          )
        } finally {
          this.setState({ isSaving: false })
        }
      })
    }
  }

  render() {
    const { location, index } = this.props

    return (
      <section className='border boder-light rounded p-3 mb-2'>
        <header className='d-flex align-items-center'>
          <div
            className='location-name-wrapper'
            onClick={() => this.setState({ expanded: !this.state.expanded })}
          >
            <i
              className={`text-muted fa fa-chevron-right ${
                this.state.expanded ? 'fa-rotate-90' : ''
              } mr-4`}
            />
            <span className='location-name'>{location ? location.name : 'New location'}</span>
          </div>

          <div>
            <div className='arrow-wrapper' onClick={() => this.reorderUp(index)}>
              <i className='fa fa-angle-up' />
            </div>
            <div className='arrow-wrapper' onClick={() => this.reorderDown(index)}>
              <i className='fa fa-angle-down' />
            </div>
          </div>
        </header>

        <Collapse className='collapsable-location-wrapper mt-2' isOpen={this.state.expanded}>
          {location && location.id && (
            <div className='text-muted mb-2'>Location ID: {location.id}</div>
          )}

          <div className='form-group'>
            <label>Location name</label>
            <Input
              value={this.state.name}
              onChange={(e) => this.setState({ name: e.target.value })}
            />
          </div>

          <div className='form-group'>
            <label>License ID</label>
            <Input
              value={
                this.state.province == CompaniesModels.Province.ON
                  ? this.state.storeId?.trim()
                  : this.state.storeId || ''
              }
              onChange={(e) =>
                this.setState({
                  storeId: e.target.value,
                })
              }
            />
          </div>

          <Row gutter={12} justify={'end'}>
            <Col md={16}>
              <div className='form-group'>
                <label>Street address</label>
                <Input
                  value={this.state.address || ''}
                  onChange={(e) => this.setState({ address: e.target.value })}
                />
              </div>
            </Col>
            <Col md={8}>
              <div className='form-group'>
                <label>Province</label>
                <div>
                  <Select<CompaniesModels.Province | null>
                    style={{ width: '100%' }}
                    defaultValue={this.state.province || null}
                    onChange={(province) => this.setState({ province: province })}
                  >
                    {Object.keys(CompaniesModels.Province).map((key) => {
                      const province: CompaniesModels.Province = CompaniesModels.Province[key]
                      return (
                        <Select.Option key={province} value={province}>
                          {province}
                        </Select.Option>
                      )
                    })}
                  </Select>
                </div>
              </div>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col md={8}>
              <div className='form-group'>
                <label>City</label>
                <Input
                  value={this.state.city || ''}
                  onChange={(e) => this.setState({ city: e.target.value })}
                />
              </div>
            </Col>
            <Col md={8}>
              <div className='form-group'>
                <label>Postal code</label>
                <Input
                  value={this.state.postalCode || ''}
                  onChange={(e) => this.setState({ postalCode: e.target.value })}
                />
              </div>
            </Col>
            <Col md={8}>
              <div className='form-group'>
                <label>Timezone</label>
                <div>
                  <Select<string | null>
                    style={{ width: '100%' }}
                    defaultValue={this.state.timezone || null}
                    onChange={(timezone) => this.setState({ timezone: timezone })}
                  >
                    <Select.Option value='America/Vancouver'>Pacific</Select.Option>
                    <Select.Option value='America/Edmonton'>Mountain</Select.Option>
                    <Select.Option value='America/Regina'>Central</Select.Option>
                    <Select.Option value='America/Toronto'>Eastern</Select.Option>
                    <Select.Option value='America/Halifax'>Atlantic</Select.Option>
                  </Select>
                </div>
              </div>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col md={12}>
              <div className='form-group'>
                <label>Phone number</label>
                <Input
                  value={this.state.phoneNumber || ''}
                  onChange={(e) => this.setState({ phoneNumber: e.target.value })}
                />
              </div>
            </Col>
            <Col md={12}>
              <div className='form-group'>
                <label>Email</label>
                <Input
                  type='email'
                  value={this.state.email || ''}
                  onChange={(e) => this.setState({ email: e.target.value })}
                />
              </div>
            </Col>
          </Row>

          <div className='form-group'>
            <Button type='primary' loading={this.state.isSaving} onClick={() => this.save()}>
              Save
            </Button>
          </div>

          {!!this.props.location && (
            <div className='form-group mt-5'>
              <h6 className='mb-2 danger-title'>
                <i className='mr-2 fa fa-exclamation-triangle' aria-hidden='true' />
                Danger zone
              </h6>
              <p className='text-muted'>
                Deleting your location will hide all location-specific data such as sales and
                inventory changes.
                <br />
                Rather than deleting, we recommend renaming locations to fit another purpose.
                <br />
                If you have accidentally deleted a location, please contact {BLAZE_COMPANY} support.
              </p>

              <Button type='danger' loading={this.state.isSaving} onClick={() => this.delete()}>
                Delete location
              </Button>
            </div>
          )}
        </Collapse>
      </section>
    )
  }
}
